import { share, Subject } from "rxjs";
import { CreateMultipointShapeOptions, EntityId, ISelectionApi, NumericFormattingParams, RiskrewardlongLineToolOverrides, ShapePoint, SupportedLineTools } from "../charting_library";
import { TVContext } from "../hub/context";
import { TVSyncer } from "./sync";
import { SyncerChange, TVSyncerTool } from "./syncitem";
import { colorTool, color_transparent, crossLineTool, dTool, fromTvPt, getColor, halign_center, halign_left, halign_right, hlTool, hrTool, lineTool, rectTool, tlTool, toTvPt, tvpoint, valign_bottom, valign_top, vlTool, zOrderType } from "../tv";
import { LineStyle, LineWidth, TxtAlign } from "../config/smpa";
import { CrossHairSync } from "./crosshair";
import { TimeFrameHelper } from "../hub/timeframe";
import { hub } from "../hub";
import { OrderConfig, TradingConfig, TradingInfoDisp, TradingOrderType } from "../config/trading";
import { isSet } from "../storage";
import { TradingHub } from "../trading/tradingHub";
import { EditingPosition } from "../trading/editingPosition";
import { EditingOrder } from "../trading/editingOrder";
import { EditingTargetOrder } from "../trading/editingTargetOrder";
import { VarFixFormatPipe } from "../../../pipes/UsdFormat.pipe";

export interface PositionChange {
    newPrice: number;
    newPos: number;
    type: SelectionType;
    order: EditingOrder;

    tvd: TVContext;
    pos: EditingPosition;
    move: IMoveOrigin
}
class priceLine extends hlTool {
    config: OrderConfig;
    text: tlTool;
    constructor(tvd: TVContext, public order: EditingOrder, config: OrderConfig) {
        super(tvd, null, null, false, null, null);
        if (this.order.pos.new)
            this.text = new tlTool(tvd, color_transparent, LineWidth.w1, null, LineStyle.solid, null);
        this.updateView(config, false);
    }


    get textTool(): lineTool {
        return this.text ?? this;
    }

    updateView(config: OrderConfig, rebuild: boolean) {
        this.config = config;
        this.width = this.config.lWidth;
        this.lineStyle = this.config.lStyle;
        this.fontSize = this.config.txtSz;
        const txtColor = getColor(this.config.col, this.config.txtOp);
        this.setColor(getColor(this.config.col), txtColor);
        this.text?.setColor(color_transparent, txtColor);

        if (this.id) {
            this.setProperties({
                linestyle: this.lineStyle,
                linecolor: this.color,
                linewidth: this.width,
            });
            if (rebuild) {
                this.updateAlign();
                this.updateText();
            }
        }
    }
    get isChanged() {
        return this.price != this.order.price;
    }
    get price() {
        return this.getShape().getPoints()[0].price;
    }
    private round(p: number, r: number = 2) {
        return p.toFixed(r);
    }
    private updateAlign() {

        let longAlign = this.order.pos.long;
        if (this.order.trd.type == TradingOrderType.Stop)
            longAlign = !longAlign;
        let halign = halign_right;
        if (this.order.pos.canRM)
            switch (this.config.txtAl) {
                case TxtAlign.Center:
                    halign = halign_center;
                    break;
                case TxtAlign.Left:
                    halign = halign_left;
                    break;
            }
        this.textTool.setAligns(longAlign ? valign_top : valign_bottom, halign, this.fontSize, true);
    }
    private updateText() {
        let txt: string = '';
        if (isSet(this.config.infoDisp, TradingInfoDisp.u_pnl))
            add(this.config, "PNL", this.order.u_pnl, '$');
        else if (isSet(this.config.infoDisp, TradingInfoDisp.r_pnl))
            add(this.config, "PNL", this.order.r_pnl, '$');

        if (isSet(this.config.infoDisp, TradingInfoDisp.s_pnl) && this.order.trd.type == TradingOrderType.TakeProfit && this.order.pos.stop)
            add(this.config, "SPNL", (this.order as EditingTargetOrder).stop_pnl, '$');

        if (this.order.trd.type != TradingOrderType.Stop && this.order.pos.canRM) {
            if (isSet(this.config.infoDisp, TradingInfoDisp.r_rr))
                add(this.config, "RR", (this.order as EditingTargetOrder).r_rr, null, 2);
            else if (isSet(this.config.infoDisp, TradingInfoDisp.u_rr))
                add(this.config, "RR", this.order.rr, null, 2);
        }
        if (this.order.trd.type != TradingOrderType.Stop) {
            if (isSet(this.config.infoDisp, TradingInfoDisp.size))
                addTxt(this.config, "Size", VarFixFormatPipe.convert(this.order.size));
            else if (isSet(this.config.infoDisp, TradingInfoDisp.sizeUsd))
                add(this.config, "Size", this.order.size * this.order.price, '$');
        }
        if (!!txt)
            this.textTool.setText(txt);

        function add(config: OrderConfig, header: string, v: number, suf: string = '', d: number = 0) {
            if (config.txtUC)
                header = header.toUpperCase();
            else
                header = header.toLocaleLowerCase();
            if (!suf)
                suf = '';
            txt = `${txt} ${header}: ${(v ?? 0).toFixed(d)}${suf}`;
        }
        function addTxt(config: OrderConfig, header: string, v: string, suf: string = '') {
            if (config.txtUC)
                header = header.toUpperCase();
            else
                header = header.toLocaleLowerCase();
            if (!suf)
                suf = '';
            txt = `${txt} ${header}: ${v}${suf}`;
        }
    }
    override clear() {
        super.clear();
        this.text?.clear();
    }
    apply(bound: IPosBound, onlyText: boolean = false) {
        this.updateAlign();
        if (!onlyText) {
            const start = { t: bound.start, p: this.order.price };
            this.createShape(start);
            this.text?.build(start, { t: bound.end, p: this.order.price });
        }
        this.updateText();
    }
}

export enum SelectionType {
    Start,
    End,
    Level,
    Move
}

export interface IPosBound {
    start: number;
    end: number;
}

export interface IMoveOrigin extends IPosBound {

    orig: tvpoint;
    origs: {
        price: number;
        order: EditingOrder;
    }[];
}


export class TradingTool {
    stopArea: rectTool;
    profitArea: rectTool;
    stopTool: priceLine;
    targetTools = new Map<EditingOrder, priceLine>();
    entryTool: priceLine;
    selectionTool: colorTool;
    selectionType: SelectionType;
    pt: tvpoint;
    hots: IMoveOrigin;
    selectedPrice: priceLine;
    ids = new Map<string, priceLine>();

    beginChangeEvent: (ev: PositionChange) => void;
    endChangeEvent: (ev: PositionChange) => void;

    constructor(public tvd: TVContext, public pos: EditingPosition, beginChangeEvent: (ev: PositionChange) => void, endChangeEvent: (ev: PositionChange) => void) {
        this.beginChangeEvent = beginChangeEvent;
        this.endChangeEvent = endChangeEvent;
    }

    updateView(rebuild = false) {
        let config = this.pos?.new ? this.config.nstop : this.config.stop;
        this.stopTool?.updateView(config, rebuild);
        config = this.pos?.new ? this.config.nentry : this.config.entry;
        this.entryTool?.updateView(config, rebuild);
        config = this.pos?.new ? this.config.nprofit : this.config.profit;
        for (const target of this.targetTools.values())
            target.updateView(config, rebuild);
        this.stopArea?.setColor(this.stopTool?.color);
        this.profitArea?.setColor(getColor(config.col));
    }
    clear() {
        this.selectionTool?.clear();
        this.selectionTool = null;
        this.selectedPrice = null;
        this.stopArea?.clear();
        this.stopArea = null;
        this.profitArea?.clear();
        this.profitArea = null;
        this.entryTool?.clear();
        this.entryTool = null;
        this.stopTool?.clear();
        this.stopTool = null;
        this.targetTools.forEach(x => x.clear());
        this.targetTools.clear();
        this.ids.clear();
    }
    private getBound(): tvpoint[] {
        if (!this.pos.lastTarget || !this.pos.stop)
            return [{ p: 0, t: this.pos.start }, { p: 0, t: this.pos.end }];

        const target = this.pos.targets[this.pos.targets.length - 1];
        const low = this.pos.long ? this.pos.stop.price : target.price;
        const high = this.pos.long ? target.price : this.pos.stop.price;
        return [{ p: low, t: this.pos.start }, { p: high, t: this.pos.end }];
    }
    private select(tool: priceLine, type: SelectionType) {
        this.selectedPrice = tool;
        this.selectionTool?.clear();
        if (tool != null)
            this.selectedPrice.getShape().setProperties({
                linecolor: 'rgba(0,0,0,0)',
                showPrice: false
            });
        const config = hub.config.chart.trading.selection;
        switch (type) {
            case SelectionType.Level: {
                if (!this.selectedPrice)
                    return;

                const t = new hlTool(this.tvd, this.selectedPrice.color, config.lWidth, false, config.lStyle);
                t.build(this.selectedPrice.getPoints()[0]);
                this.selectionTool = t;
            } break;
            case SelectionType.Start: {
                if (this.selectedPrice) {
                    const t = new crossLineTool(this.tvd, this.selectedPrice.color, config.lWidth, config.lStyle);
                    t.build({ t: this.pos.start, p: this.selectedPrice.price });
                    this.selectionTool = t;
                } else {
                    const t = new vlTool(this.tvd, this.entryTool.color, config.lWidth, false, config.lStyle);
                    t.build({ t: this.pos.start, p: this.entryTool.price });
                    this.selectionTool = t;
                }
            } break;
            case SelectionType.End: {
                if (this.selectedPrice) {
                    const t = new crossLineTool(this.tvd, this.selectedPrice.color, config.lWidth, config.lStyle);
                    t.build({ t: this.pos.end, p: this.selectedPrice.price });
                    this.selectionTool = t;
                } else {
                    const t = new vlTool(this.tvd, this.entryTool.color, config.lWidth, false, config.lStyle);
                    t.build({ t: this.pos.end, p: this.entryTool.price });
                    this.selectionTool = t;
                }
            } break;
            case SelectionType.Move: {
                this.hots = {
                    start: this.pos.start,
                    end: this.pos.end,
                    orig: this.pt,
                    origs: []
                };
                for (const price of this.ids.values())
                    this.hots.origs.push({
                        order: price.order,
                        price: price.order.price
                    });
                const t = new rectTool(this.tvd, config.col, config.lWidth, null, false, false);
                t.buildFromPts(this.getBound());
                this.selectionTool = t;
            } break;
        }

        this.selectionType = type;
    }

    drag(pt: tvpoint) {
        this.pt = pt;
        if (!this.selectionTool)
            return;

        const ev: PositionChange = {
            tvd: this.tvd,
            pos: this.pos,
            newPos: this.pt.t,
            type: this.selectionType,
            newPrice: this.pt.p,
            order: this.selectedPrice?.order,
            move: this.hots
        };

        this.beginChangeEvent(ev);

        switch (this.selectionType) {
            case SelectionType.Level: {
                const newPt = { t: this.selectedPrice.getPoints()[0].t, p: this.selectedPrice.order.price };
                this.selectionTool.setPoints([newPt]);
            } break;
            case SelectionType.Start:
            case SelectionType.End: {
                const newPt = { t: this.pt.t, p: this.selectedPrice?.order?.price ?? this.entryTool.order.price };
                this.selectionTool.setPoints([newPt]);
            } break;
            case SelectionType.Move:
                this.selectionTool.setPoints(this.getBound());
                break;
        }
        const bound: IPosBound = {
            start: this.pos.start,
            end: this.pos.end
        };
        this.buildPosition(bound);
        for (const t of this.ids.values())
            t.apply(bound);
        this.endChangeEvent(ev);
    }
    beginDrag(ev: MouseEvent) {

        if (this.selectionTool || !this.tvd.isTvReady())
            return false;

        const tf = this.tvd.getTimeFrameInfo();
        if (this.pos.tf != null) {
            this.pos.start = TimeFrameHelper.adjustTVTime(this.tvd, this.pos.start, this.pos.tf);
            this.pos.end = TimeFrameHelper.adjustTVTime(this.tvd, this.pos.end, this.pos.tf);
        }

        this.pos.tf = tf;

        let type: SelectionType;
        const bar = (this.tvd.getBar(0).time - this.tvd.getBar(1).time) / 1000;
        let left = this.pos.start - bar;
        let right = this.pos.start + bar;
        let inside = false;
        if (this.pt.t >= left && this.pt.t <= right)
            type = SelectionType.Start;
        else {
            let eleft = this.pos.end - bar;
            let eright = this.pos.end + bar;
            if (this.pt.t >= eleft && this.pt.t <= eright)
                type = SelectionType.End;
            else if (this.pt.t >= left) {
                type = SelectionType.Level;
                inside = this.pt.t <= eright;
            }
        }
        if (type != null) {
            const pane = this.tvd.tv.activeChart().getPanes()[0];
            const height = pane.getHeight();
            const priceRange = pane.getMainSourcePriceScale().getVisiblePriceRange();
            if (!priceRange)
                return false;
            let ratio = 3 * (priceRange.to - priceRange.from) / height;
            if (isInRange(this.stopTool, this.pt))
                this.select(this.stopTool, type);
            else if (isInRange(this.entryTool, this.pt))
                this.select(this.entryTool, type);
            else for (let tp of this.targetTools.values())
                if (isInRange(tp, this.pt)) {
                    this.select(tp, type);
                    break;
                }
            if (this.selectionTool == null) {
                const bound = this.getBound();
                if (this.pt.p >= bound[0].p && this.pt.p <= bound[1].p)
                    if (inside)
                        this.select(null, SelectionType.Move);
                    else
                        this.select(null, type);
            }

            function isInRange(priceLine: priceLine, pt: tvpoint) {
                if (!priceLine)
                    return false;
                const price = priceLine.price;
                if (price >= pt.p - ratio && price <= pt.p + ratio)
                    return true;
                return false;
            }
        }
        return this.selectionTool != null;
    }
    deselect() {
        if (!this.selectionTool)
            return;
        this.selectedPrice?.getShape()?.setProperties({
            linecolor: this.selectedPrice.color,
            showPrice: true
        });
        this.selectionTool?.clear();
        this.selectionTool = null;
        this.selectedPrice = null;

    }
    endDrag() {
        if (!this.selectionTool)
            return false;
        this.deselect();
        return this.selectionTool == null;
    }
    get config() {
        return hub.config.chart.trading;
    }
    build() {
        if (!this.tvd.isTvReady())
            return;

        if (this.pos.start == null) {
            this.pos.tf = this.tvd.getTimeFrameInfo();
            const start = this.tvd.getTime(30) / 1000;
            const end = this.tvd.getTime(0) / 1000;
            this.pos.start = end;
            this.pos.end = end + (end - start);
        }


        const bound: IPosBound = {
            start: this.pos.start,
            end: this.pos.end
        };

        const tf = this.tvd.getTimeFrameInfo();
        if (this.pos.tf != null && tf.o > this.pos.tf.o) {
            bound.start = TimeFrameHelper.adjustTVTime(this.tvd, bound.start, this.pos.tf);
            bound.end = TimeFrameHelper.adjustTVTime(this.tvd, bound.end, this.pos.tf);
        }

        this.buildPosition(bound);

        this.entryTool = this.addTool(bound, this.entryTool, this.pos.entry, this.pos.new ? this.config.nentry : this.config.entry);
        if (this.pos.stop)
            this.stopTool = this.addTool(bound, this.stopTool, this.pos.stop, this.pos.new ? this.config.nstop : this.config.stop);
        else {
            this.stopTool?.clear();
            this.stopTool = null;
        }
        this.targetTools.forEach(line => {
            this.ids.delete(line.id);
            line.clear();
            this.targetTools.delete(line.order);
        });

        if (this.pos.lastTarget) {
            const targets = new Set<EditingOrder>(this.pos.targets);
            this.pos.targets.forEach(order => {
                let line = this.targetTools.get(order);
                line = this.addTool(bound, line, order, this.pos.new ? this.config.nprofit : this.config.profit);
                this.targetTools.set(order, line);
            });
        }
    }

    private addTool(bound: IPosBound, tool: priceLine, order: EditingOrder, config: OrderConfig): priceLine {
        if (tool && tool.order.pos != this.pos) {
            this.ids.delete(tool.id);
            tool.clear();
            tool = null;
        }
        tool ??= new priceLine(this.tvd, order, config);
        tool.config = config;
        tool.cachable = false;
        tool.apply(bound);
        this.ids.set(tool.id, tool);
        return tool;
    }
    private buildPosition(bound: IPosBound) {
        let rebuild = !this.stopArea || this.pos.start == null;
        if ((!this.stopArea) != (!this.pos?.stop))
            rebuild = true;
        if ((!this.profitArea) != (!this.pos?.lastTarget))
            rebuild = true;
        if (rebuild) {
            this.stopArea?.clear();
            this.stopArea = null;

            this.profitArea?.clear();
            this.profitArea = null;

            if (this.pos?.stop && this.pos?.lastTarget) {
                this.stopArea = new rectTool(this.tvd, getColor(this.config.stop.col), LineWidth.w0, color_transparent, true, false, false);
                this.stopArea.zOrder = zOrderType.bottom;

                this.profitArea = new rectTool(this.tvd, getColor(this.config.profit.col), LineWidth.w0, color_transparent, true, false, false);
                this.profitArea.zOrder = zOrderType.bottom;
            }
        }

        const pt0: tvpoint = {
            t: bound.start,
            p: this.pos.entry.price
        };
        if (this.stopArea) {
            const pt1: tvpoint = {
                t: bound.end,
                p: this.pos.stop.price
            };
            this.stopArea?.build(pt0, pt1);
            this.stopArea?.sendToBack();
        }
        if (this.profitArea) {
            const pt1: tvpoint = {
                t: bound.end,
                p: this.pos.lastTarget.price
            };
            this.profitArea?.build(pt0, pt1);
            this.profitArea?.sendToBack();
        }
    }
}


export class TradingSync extends TVSyncerTool {
    private trading: TradingTool;

    private beginChange: Subject<PositionChange> = new Subject<PositionChange>();
    private endChange: Subject<PositionChange> = new Subject<PositionChange>();
    pos: EditingPosition;
    editing: boolean = true;
    getBeginChange = () => this.beginChange.asObservable();
    getEndChange = () => this.endChange.asObservable();
    private get holder() { return hub.trading.account; }
    constructor(syncer: TVSyncer, tvd: TVContext) {
        super(syncer, tvd);
    }
    public setEditing(b: boolean) {
        this.editing = b;
    }
    public refresh() {
        if (this.holder.current?.tid == this.tvd.tid)
            this.pos = this.holder.current;
        else
            this.pos = null;
        if (!this.pos) {
            this.trading?.clear();
            this.trading = null;
        } else {
            if (this.pos != this.trading?.pos) {
                this.trading?.clear();
                this.trading = new TradingTool(this.tvd, this.pos, ev => this.beginChange.next(ev), ev => this.endChange.next(ev));
            } else {
                this.trading.pos = this.pos;
            }
            this.trading.build();
        }
    }
    deselect() {
        this.trading?.deselect();
    }
    delete(pos: EditingPosition) {
        if (!pos || this.trading?.pos == pos) {
            this.trading?.clear();
            this.trading = null;
        }
    }
    override onEndChange(type: SyncerChange) {
        if (type != SyncerChange.Symbol)
            return;
        this.refresh();
    }
    override updateView(rebuild: boolean) {
        this.trading?.updateView(rebuild);
    }
    override onInit(): void {
        super.onInit();
        this.tvd.tv.activeChart().crossHairMoved().subscribe(
            null,
            (p: TradingView.CrossHairMovedEventParams) => {
                if (!this.editing)
                    return;
                this.trading?.drag(CrossHairSync.convertToUtc(this.tvd, p).pt);
            }
        );
        this.tvd.iframe.contentWindow.addEventListener("mousedown", (e) => {
            if (!this.editing)
                return;
            if (this.trading?.beginDrag(e))
                e.stopPropagation();
        }, true);
        this.tvd.iframe.contentWindow.addEventListener("mouseup", (e) => {
            if (!this.editing)
                return;
            this.trading?.endDrag();
        }, true);

        hub.trading.onInited(this);
    }
    override dispose(): void {
        this.beginChange.complete();
        this.endChange.complete();
    }
    override clear() {
        this.trading?.clear();
    }
}
